import { useMutation, useQuery } from "@tanstack/react-query";
import { useId } from "react";
import { useTranslation } from "react-i18next";
import { Switch } from "@/components/ui/switch";
import { useModule } from "@/lib/modules";
import { useTRPC } from "@/lib/trpc";

/**
 * A supplier's "Does service work" switch (`SupplierExtra`): flagged suppliers are offered as who
 * performed a work order. It saves on its own, apart from the dialog's Save.
 */
export function SupplierServiceSwitch({ supplierId }: { supplierId: string }) {
  const { t } = useTranslation();
  const trpc = useTRPC();
  const labelId = useId();
  const hintId = useId();
  const active = useModule("vehicleManagement");
  const { data: providers } = useQuery({
    ...trpc.vehicleManagement.serviceProviders.queryOptions(),
    enabled: active,
  });
  const set = useMutation(trpc.vehicleManagement.setDoesService.mutationOptions());
  if (!active || !providers) return null;
  const on = set.isPending ? set.variables.value : providers.some((p) => p.id === supplierId);
  return (
    <div className="flex items-start justify-between gap-4 rounded-lg border border-border px-3 py-2.5">
      <div className="min-w-0">
        <div id={labelId} className="text-sm font-medium">
          {t("vehicleManagement.doesService")}
        </div>
        <p id={hintId} className="text-xs text-muted-foreground">
          {t("vehicleManagement.doesServiceHint")}
        </p>
      </div>
      <Switch
        checked={on}
        disabled={set.isPending}
        onCheckedChange={(value) => set.mutate({ supplierId, value })}
        aria-labelledby={labelId}
        aria-describedby={hintId}
        className="mt-0.5 shrink-0"
      />
    </div>
  );
}
